import React, { RefObject } from 'react';
import T from 'i18n';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  ListItem,
  Menu,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PerfectScrollbar from 'react-perfect-scrollbar';
import SearchInputLocations from './SearchInputLocations';
import { isMobile } from 'utils/device';
import { removeIndexFolders } from 'utils/foldersStructuring';
import { useSelector } from 'react-redux';
import { selectFolders } from 'common/selectors';

interface TrialBundlesMenuProps {
  value: any;
  onChange: (value: any[]) => void;
  anchorEl: any;
  isOpen: boolean;
  onClose: (e: any) => void;
  noClear?: boolean;
  width?: string;
  /**
   * UI variant. Defaults to existing visuals to avoid affecting other screens.
   * `outputFormatLike` is intended for AskAISSE only.
   */
  variant?: 'default' | 'outputFormatLike';
}

const TrialBundlesMenu = React.forwardRef((props: TrialBundlesMenuProps, ref) => {
  const {
    value,
    onChange,
    anchorEl,
    isOpen,
    onClose,
    noClear,
    width = 'auto',
    variant = 'default',
  } = props;
  const folders = useSelector(selectFolders);
  const trialBundlesWithoutIndexFolder = removeIndexFolders(folders['trialbooks']);
  const addTrialbooksList =
    trialBundlesWithoutIndexFolder && trialBundlesWithoutIndexFolder.length > 0;
  const addTeamBundlesList = folders && folders['teamBundles'] && folders['teamBundles'].length > 0;
  const addPrivateBundlesList =
    folders && folders['privateBundles'] && folders['privateBundles'].length > 0;

  const filtersScrollbarRef = React.useRef<any>(null);
  const isOutputFormatLike = variant === 'outputFormatLike';
  const [searchText, setSearchText] = React.useState('');
  const hasSelections = Array.isArray(value) && value.length > 0;

  React.useEffect(() => {
    if (!isOutputFormatLike) return;
    if (!isOpen) setSearchText('');
  }, [isOpen, isOutputFormatLike]);

  const normalizedQuery = searchText.trim().toLowerCase();

  const filterLocationsTree = React.useCallback((items: any[], query: string) => {
    if (!query) return items;
    const visit = (node: any): any | null => {
      const name = String(node?.name ?? '').toLowerCase();
      const matchesSelf = name.includes(query);
      const children = Array.isArray(node?.folders) ? node.folders : null;
      if (!children || children.length === 0) {
        return matchesSelf ? node : null;
      }
      const filteredChildren = children.map(visit).filter(Boolean);
      if (matchesSelf || filteredChildren.length > 0) {
        return { ...node, folders: filteredChildren };
      }
      return null;
    };
    return items.map(visit).filter(Boolean);
  }, []);

  const filteredLocationsList = React.useMemo(() => {
    // Preserve existing grouping, but hide empty groups when searching.
    const groups: Array<{ header: any; items: any[] }> = [];

    if (addTrialbooksList) {
      groups.push({
        header: { id: 'trialBundle', name: 'Trial Bundles' },
        items: trialBundlesWithoutIndexFolder || [],
      });
    }
    if (addTeamBundlesList) {
      groups.push({
        header: { id: 'teamBundles', name: 'Team Bundles' },
        items: folders['teamBundles'] || [],
      });
    }
    if (addPrivateBundlesList) {
      groups.push({
        header: { id: 'privateBundles', name: 'Private Bundles' },
        items: folders['privateBundles'] || [],
      });
    }

    if (!normalizedQuery) {
      return groups.flatMap((g) => [g.header, ...g.items]).filter(Boolean);
    }

    return groups
      .map((g) => ({ ...g, items: filterLocationsTree(g.items, normalizedQuery) }))
      .filter((g) => g.items.length > 0)
      .flatMap((g) => [g.header, ...g.items])
      .filter(Boolean);
  }, [
    addPrivateBundlesList,
    addTeamBundlesList,
    addTrialbooksList,
    filterLocationsTree,
    folders,
    normalizedQuery,
    trialBundlesWithoutIndexFolder,
  ]);

  return (
    <Menu
      ref={ref as RefObject<HTMLDivElement>}
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      disableRestoreFocus={isOutputFormatLike}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      sx={{
        ...(isOutputFormatLike
          ? {
              maxWidth: isMobile ? '90vw' : '520px',
              '& .MuiPaper-root': {
                marginTop: '-8px', // Small offset to appear just above the chip
              },
              '& .TrialBundlesMenuScrollbar': {
                overflowX: 'hidden',
              },
              '& .TrialBundlesMenuScrollbar .ps__rail-x': {
                display: 'none',
              },
              '& .TrialBundlesMenuScrollbar .ps__thumb-x': {
                display: 'none',
              },
              '& .MuiListItem-root:not(.TrialBundlesMenuHeader)': {
                borderRadius: '10px',
                mx: 0.5,
                my: 0.25,
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.04)',
                },
              },
            }
          : {
              width,
              maxWidth: isMobile ? '90vw' : '60vw',
              '& .MuiPaper-root': {
                marginTop: '-8px', // Small offset to appear just above the chip
              },
            }),
      }}
      slotProps={{
        paper: {
          style: isOutputFormatLike
            ? undefined
            : {
                width: '20vw',
              },
          sx: isOutputFormatLike
            ? {
                minWidth: 320,
                maxWidth: isMobile ? '90vw' : 520,
                maxHeight: '70vh',
                overflow: 'hidden',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 16px 44px rgba(15, 23, 42, 0.14)',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                padding: '8px',
              }
            : undefined,
        },
      }}
      MenuListProps={
        isOutputFormatLike
          ? {
              dense: true,
              sx: {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                p: 0,
                overflow: 'hidden',
                overflowX: 'hidden',
              },
            }
          : undefined
      }
    >
      <Box>
        <ListItem
          className="TrialBundlesMenuHeader"
          disableGutters={isOutputFormatLike}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            ...(isOutputFormatLike
              ? {
                  px: 1,
                  py: 0.5,
                }
              : undefined),
          }}
        >
          <Typography
            sx={{
              color: isOutputFormatLike ? 'text.secondary' : 'GrayText',
              fontWeight: isOutputFormatLike ? 600 : undefined,
              fontSize: isOutputFormatLike ? '1.05rem' : undefined,
            }}
          >
            Locations
          </Typography>
          {!noClear && (
            <Button
              variant={isOutputFormatLike ? 'text' : 'contained'}
              size="small"
              disabled={isOutputFormatLike ? !hasSelections : false}
              onClick={() => {
                onChange([]);
              }}
              sx={
                isOutputFormatLike
                  ? {
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderRadius: '10px',
                      px: 1,
                      py: 0.5,
                      minWidth: 'auto',
                    }
                  : undefined
              }
            >
              {T.translate('common.clear')}
            </Button>
          )}
        </ListItem>
        {isOutputFormatLike && (
          <>
            <Box sx={{ px: 1, pb: 0.75 }}>
              <TextField
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search locations…"
                size="small"
                fullWidth
                autoFocus
                inputProps={{ 'aria-label': 'Search locations' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d1d5db',
                    borderWidth: '1px',
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#999999',
                    borderWidth: '1px',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#999999',
                    borderWidth: '1px',
                  },
                }}
              />
            </Box>
            <Divider sx={{ my: 0.5, borderColor: '#e5e7eb' }} />
          </>
        )}
      </Box>
      <PerfectScrollbar
        options={isOutputFormatLike ? { suppressScrollX: true } : undefined}
        style={isOutputFormatLike ? { flex: 1, minHeight: 0 } : { maxHeight: '70vh' }}
        className={isOutputFormatLike ? 'TrialBundlesMenuScrollbar' : undefined}
        containerRef={(el) => (filtersScrollbarRef.current = el)}
      >
        {filteredLocationsList.length === 0 ? (
          <Box sx={{ px: 1, py: 1 }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
              No matching locations
            </Typography>
          </Box>
        ) : (
          <SearchInputLocations
            locationsMenu={filteredLocationsList}
            filters={{ location: value, files: [] }}
            handleChangeFilters={(val: any) => onChange(val.location)}
            isAnswersMode={false}
            filtersScrollbarRef={filtersScrollbarRef}
          />
        )}
      </PerfectScrollbar>
    </Menu>
  );
});

export default TrialBundlesMenu;
